import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useMemo, useRef, useState } from "react";
import { auth, firestore } from "@/config/firebase";
import { AuthContext } from "@/features/auth/authContext";
import {
  clearAccountActiveInSession,
  isAccountActiveInSession,
  markAccountActiveInSession
} from "@/features/auth/authSession";
import {
  completeGoogleRedirectSignIn,
  getGoogleAuthErrorMessage,
  syncCurrentUserActivation,
  type GoogleAuthCompletionResult
} from "@/features/auth/authService";
import type { AuthState, UserProfile } from "@/features/auth/types";

type AuthProviderProps = {
  children: React.ReactNode;
};

function isActiveProfile(profile: UserProfile | null) {
  return profile?.status === "active";
}

function redirectAfterGoogleCompletion(result: GoogleAuthCompletionResult) {
  const currentPath = window.location.pathname;

  if (result.profileResult.status === "active") {
    markAccountActiveInSession(result.user.uid);

    if (currentPath === "/entrar" || currentPath === "/cadastro") {
      window.location.replace("/painel");
    }
    return;
  }

  if (!result.profileResult.created) {
    return;
  }

  const params = new URLSearchParams();

  params.set("cadastro", "google");
  params.set("email", result.profileResult.email);
  params.set("emailDelivery", result.profileResult.emailDelivery);

  const query = params.toString();
  window.location.replace(`/verificar-conta${query ? `?${query}` : ""}`);
}

export function AuthProvider({ children }: AuthProviderProps) {
  const activationSyncAttemptKeysRef = useRef(new Set<string>());
  const [state, setState] = useState<AuthState>({
    firebaseUser: null,
    profile: null,
    loading: true,
    activationResolved: false,
    activationStatus: null,
    isActive: false
  });

  useEffect(() => {
    void completeGoogleRedirectSignIn()
      .then((result) => {
        if (result) {
          redirectAfterGoogleCompletion(result);
        }
      })
      .catch((error) => {
        sessionStorage.setItem("geoanalytics.googleAuthError", getGoogleAuthErrorMessage(error));
        setState((current) => ({ ...current, loading: false, activationResolved: true }));

        if (window.location.pathname === "/entrar") {
          window.location.replace("/entrar?google=erro");
          return;
        }

        if (
          window.location.pathname === "/cadastro" ||
          window.location.pathname === "/verificar-conta"
        ) {
          return;
        }

        window.location.replace("/cadastro?google=erro");
      });
  }, []);

  useEffect(() => {
    let unsubscribeProfile: (() => void) | undefined;

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      unsubscribeProfile?.();
      unsubscribeProfile = undefined;

      if (!user) {
        activationSyncAttemptKeysRef.current.clear();
        setState({
          firebaseUser: null,
          profile: null,
          loading: false,
          activationResolved: true,
          activationStatus: null,
          isActive: false
        });
        return;
      }

      setState((current) => ({
        ...current,
        firebaseUser: user,
        loading: true,
        activationResolved: false,
        activationStatus: null
      }));

      unsubscribeProfile = onSnapshot(
        doc(firestore, "users", user.uid),
        (snapshot) => {
          const profile = snapshot.exists() ? (snapshot.data() as UserProfile) : null;

          if (isActiveProfile(profile)) {
            markAccountActiveInSession(user.uid);
          } else if (profile?.status === "suspended" || profile?.status === "disabled") {
            clearAccountActiveInSession(user.uid);
          }

          const activeInSession = isAccountActiveInSession(user.uid);
          const accountIsActive = isActiveProfile(profile) || activeInSession;
          const activationSyncAttemptKey = `${user.uid}:${profile?.status ?? "missing"}:${
            user.emailVerified ? "verified" : "unverified"
          }`;
          const canAttemptActivationSync =
            !accountIsActive &&
            profile?.status !== "suspended" &&
            profile?.status !== "disabled" &&
            !activationSyncAttemptKeysRef.current.has(activationSyncAttemptKey);

          if (canAttemptActivationSync) {
            activationSyncAttemptKeysRef.current.add(activationSyncAttemptKey);
            setState({
              firebaseUser: user,
              profile,
              loading: true,
              activationResolved: false,
              activationStatus: profile?.status ?? null,
              isActive: false
            });

            void syncCurrentUserActivation()
              .then((result) => {
                if (result.status === "active") {
                  markAccountActiveInSession(user.uid);
                  setState((current) =>
                    current.firebaseUser?.uid === user.uid
                      ? {
                          ...current,
                          loading: false,
                          activationResolved: true,
                          activationStatus: "active",
                          isActive: true
                        }
                      : current
                  );
                  return;
                }

                setState((current) =>
                  current.firebaseUser?.uid === user.uid
                    ? {
                        ...current,
                        profile,
                        loading: false,
                        activationResolved: true,
                        activationStatus: result.status ?? profile?.status ?? null,
                        isActive: false
                      }
                    : current
                );
              })
              .catch(() => {
                setState((current) =>
                  current.firebaseUser?.uid === user.uid
                    ? {
                        ...current,
                        profile,
                        loading: false,
                        activationResolved: true,
                        activationStatus: activeInSession ? "active" : (profile?.status ?? null),
                        isActive: activeInSession
                      }
                    : current
                );
              });
            return;
          }

          setState({
            firebaseUser: user,
            profile,
            loading: false,
            activationResolved: true,
            activationStatus: accountIsActive ? "active" : (profile?.status ?? null),
            isActive: accountIsActive
          });
        },
        () => {
          setState({
            firebaseUser: user,
            profile: null,
            loading: false,
            activationResolved: true,
            activationStatus: isAccountActiveInSession(user.uid) ? "active" : null,
            isActive: isAccountActiveInSession(user.uid)
          });
        }
      );
    });

    return () => {
      unsubscribeProfile?.();
      unsubscribeAuth();
    };
  }, []);

  const value = useMemo(() => state, [state]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
